import { useState, useEffect, useCallback, useRef } from 'react';
import { useLazyGetRawContactListQuery } from '../../../../../services/cellcast.v2';
import { Contact, GetContactListResponse } from '../type';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { defaultPage, defaultPageSize } from '../../../../Utils/Constant/WalletCard';
import { useI18n } from '../../../../Utils/_helpers/useI18n';
import {
  SORT_ASC,
  SORT_DESC,
  PARAM_PAGE,
  PARAM_LIMIT,
  PARAM_SEARCH,
  PARAM_SORT_BY,
  PARAM_SORT_ORDER
} from '../constants';

// Constants
const LOADING_DELAY = 300; // ms
const DEBOUNCE_DELAY = 600; // ms

export interface TotalLeads {
  total: number;
  totalLeads: number;
  totalLeadGrowth: number;
  totalPages: number;
  totalWarmUp: number;
  totalWarmUpGrowth: number;
  totalBites: number;
  totalBitesGrowth: number;
  totalSales: number;
  totalSalesGrowth: number;
  countValid: number;
}

export const useContactData = (initialFilter = 'all') => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [previousContacts, setPreviousContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState<TotalLeads>({
    total: 0,
    totalLeads: 0,
    totalLeadGrowth: 0,
    totalPages: 0,
    totalWarmUp: 0,
    totalWarmUpGrowth: 0,
    totalBites: 0,
    totalBitesGrowth: 0,
    totalSales: 0,
    totalSalesGrowth: 0,
    countValid: 0
  });
  const [page, setPage] = useState({
    page: defaultPage,
    size: defaultPageSize,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermDebounced, setSearchTermDebounced] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [previousSearchQuery, setPreviousSearchQuery] = useState('');

  // Filter state
  const [filterValue, setFilterValue] = useState(initialFilter);

  // Sorting states
  const [sortKey, setSortKey] = useState<keyof Contact | ''>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(SORT_ASC);
  const [previousSortKey, setPreviousSortKey] = useState<keyof Contact | ''>('');
  const [previousSortDirection, setPreviousSortDirection] = useState<'asc' | 'desc'>(SORT_ASC);

  const [pendingFetch, setPendingFetch] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [getRawContactList, { isLoading }] = useLazyGetRawContactListQuery();

  // Tracking refs
  const isInitialMount = useRef(true);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchParamsRef = useRef({
    page: defaultPage,
    size: defaultPageSize,
    search: '',
    filter: 'all',
    sortKey: '',
    sortDirection: SORT_ASC as 'asc' | 'desc'
  });

  // Handle debounced search
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      if (searchTerm !== previousSearchQuery) {
        setSearchTermDebounced(searchTerm.trim());
        setPreviousSearchQuery(searchTerm);

        setPage(prev => ({
          ...prev,
          page: defaultPage
        }));
      }
    }, DEBOUNCE_DELAY);

    setSearchTimeout(newTimeout);

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, previousSearchQuery]);

  // Check if fetch params have changed
  const shouldFetchData = useCallback((currentParams: {
    page: number;
    size: number;
    search: string;
    filter: string;
    sortKey: string | keyof Contact;
    sortDirection: 'asc' | 'desc';
  }) => {
    const lastParams = lastFetchParamsRef.current;

    return (
      currentParams.page !== lastParams.page ||
      currentParams.search !== lastParams.search ||
      currentParams.filter !== lastParams.filter ||
      currentParams.sortKey !== lastParams.sortKey ||
      currentParams.sortDirection !== lastParams.sortDirection
    );
  }, []);

  // Fetch contact data
  const fetchContacts = useCallback(async (options?: {
    forceRefresh?: boolean,
    newPage?: number,
    newSortKey?: keyof Contact | '',
    newSortDirection?: 'asc' | 'desc',
    isSearching?: boolean,
    newFilter?: string
  }) => {
    // Clear any pending fetch timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    const pageToUse = options?.newPage !== undefined ? options.newPage : page.page;
    const keyToUse = options?.newSortKey !== undefined ? options.newSortKey : sortKey;
    const directionToUse = options?.newSortDirection !== undefined ? options.newSortDirection : sortDirection;
    const filterToUse = options?.newFilter !== undefined ? options.newFilter : filterValue;

    // Check if we need to update state based on options
    if (options?.newPage !== undefined && options.newPage !== page.page) {
      setPage(prev => ({ ...prev, page: options.newPage as number }));
    }

    if (options?.newSortKey !== undefined && options.newSortKey !== sortKey) {
      setSortKey(options.newSortKey);
    }

    if (options?.newSortDirection !== undefined && options.newSortDirection !== sortDirection) {
      setSortDirection(options.newSortDirection);
    }

    if (options?.newFilter !== undefined && options.newFilter !== filterValue) {
      setFilterValue(options.newFilter);
    }

    // Current fetch params
    const currentParams = {
      page: pageToUse,
      size: page.size,
      search: searchTermDebounced,
      filter: filterToUse,
      sortKey: keyToUse,
      sortDirection: directionToUse,
    };

    // Check if we need to fetch
    if (!options?.forceRefresh && !shouldFetchData(currentParams)) {
      return;
    }

    // Update last fetch params
    lastFetchParamsRef.current = { ...currentParams };

    // Start loading
    setPendingFetch(true);

    // Only show loading for the table
    setTableLoading(true);

    // Save previous state for potential rollback
    setPreviousContacts(contacts);
    setPreviousSortKey(sortKey);
    setPreviousSortDirection(sortDirection);

    try {
      const response = await getRawContactList({
        [PARAM_PAGE]: pageToUse,
        [PARAM_LIMIT]: page.size,
        [PARAM_SEARCH]: searchTermDebounced,
        filter: filterToUse,
        [PARAM_SORT_BY]: keyToUse,
        [PARAM_SORT_ORDER]: directionToUse
      }) as GetContactListResponse;

      await new Promise(resolve => setTimeout(resolve, LOADING_DELAY));

      const data: Contact[] = response?.data?.data?.items || [];
      setTotal({
        total: response?.data?.data?.total || 0,
        totalLeads: response?.data?.data?.totalLeads || 0,
        totalLeadGrowth: response?.data?.data?.totalLeadGrowth || 0,
        totalPages: response?.data?.data?.totalPages || 0,
        totalWarmUp: response?.data?.data?.totalWarmUp || 0,
        totalWarmUpGrowth: response?.data?.data?.totalWarmUpGrowth || 0,
        totalBites: (response as any)?.data?.data?.totalBites || 0,
        totalBitesGrowth: (response as any)?.data?.data?.totalBitesGrowth || 0,
        totalSales: (response as any)?.data?.data?.totalSales || 0,
        totalSalesGrowth: (response as any)?.data?.data?.totalSalesGrowth || 0,
        countValid: response?.data?.data?.countValid || 0
      });
      setContacts(data);
    } catch (error: any) {
      setContacts(previousContacts);
      toast.error(error?.data?.message || t('RAW_CONTACT_COLLECTION.FETCH_ERROR'));
    } finally {
      // Always turn off table loading
      setTableLoading(false);
      setPendingFetch(false);
    }
  }, [
    getRawContactList,
    page.size,
    page.page,
    searchTermDebounced,
    sortKey,
    sortDirection,
    previousContacts,
    contacts,
    shouldFetchData,
    t
  ]);

  const handleSort = useCallback((key: keyof Contact | '') => {
    let newDirection: 'asc' | 'desc' = SORT_ASC;

    if (sortKey === key) {
      newDirection = sortDirection === SORT_ASC ? SORT_DESC : SORT_ASC;
    }

    fetchContacts({
      newPage: page.page,
      newSortKey: key,
      newSortDirection: newDirection,
      forceRefresh: true
    });
  }, [fetchContacts, sortKey, sortDirection, page.page]);

  const handlePageChange = useCallback((selectedPage: number) => {
    fetchContacts({
      newPage: selectedPage + 1,
      forceRefresh: true
    });
  }, [fetchContacts]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Initial fetch
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchContacts({ forceRefresh: true });
    }
  }, [fetchContacts]);

  // Auto-fetch when search term changes
  useEffect(() => {
    // Skip first render as it's handled by the initial fetch
    if (isInitialMount.current) {
      return;
    }

    // Clear any pending fetch
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Set a small delay to prevent too many requests
    fetchTimeoutRef.current = setTimeout(() => {
      if (!pendingFetch) {
        // Mark this as a search request
        fetchContacts({ isSearching: true });
      }
      fetchTimeoutRef.current = null;
    }, 50);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [searchTermDebounced, pendingFetch, fetchContacts]);


  return {
    contacts,
    total,
    page,
    searchTerm,
    setSearchTerm,
    filterValue,
    setFilterValue,
    sortKey,
    sortDirection,
    isLoading: tableLoading,
    clearSearch,
    handleSort,
    handlePageChange,
    fetchContacts
  };
};
